import { createFileRoute } from "@tanstack/react-router";
import { useState } from "react";
import { ArrowRight, BadgeCheck, CheckCircle2, Crown, Info, Wallet } from "lucide-react";
import { useAuthStore } from "@/store/authStore";
import PageHeader from "@/components/shared/PageHeader";
import SectionCard from "@/components/shared/SectionCard";
import { PageSkeleton } from "@/components/shared/Skeletons";
import { TierBadge } from "@/components/shared/TierBadge";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { taskpartStudentTiers } from "@/data/taskpartProduct";
import { usePricing } from "@/hooks/usePricing";
import PaymentModal from "@/components/shared/PaymentModal";
import { startSnippeCheckout, usePaymentReturn } from "@/lib/payments";
import { cn } from "@/lib/utils";

export const Route = createFileRoute("/student/wallet/")({
  component: StudentWallet,
});

function StudentWallet() {
  const { user } = useAuthStore();
  const { data: pricing, isLoading: pricingLoading } = usePricing();
  const [upgradeOpen, setUpgradeOpen] = useState(false);
  const [selectedTier, setSelectedTier] = useState("");

  const currentTier = user?.membershipTier || "free";
  const tiers = pricing?.tiers || taskpartStudentTiers;

  const getAmountForTier = (tierId: string) => {
    if (tierId === "verified") return parseInt(pricing?.verifiedPrice || "10000");
    if (tierId === "premium") return parseInt(pricing?.premiumPrice || "50000");
    return 0;
  };

  const handleUpgrade = (tierId: string) => {
    setSelectedTier(tierId);
    setUpgradeOpen(true);
  };

  // Paying for a tier goes through Snippe checkout; the API upgrades the
  // membership once Snippe confirms the payment.
  const handlePaymentSuccess = () =>
    startSnippeCheckout({
      type: selectedTier === "premium" ? "premium" : "verification",
      returnPath: "/student/wallet",
    });

  usePaymentReturn((status) => {
    if (status === "completed" || status === "verified") {
      window.location.reload();
    }
  });

  return (
    <div>
      <PageHeader
        title="Wallet & tier"
        subtitle="Manage your earnings, balance, and institutional access level."
        breadcrumbs={[{ label: "Dashboard", path: "/student/dashboard" }, { label: "Wallet" }]}
      />

      <div className="grid gap-6 lg:grid-cols-3">
        {/* Left column: balance */}
        <div className="space-y-4">
          <section
            aria-label="Balance"
            className="flex flex-col gap-8 rounded-lg border border-border bg-card p-5 md:p-6"
          >
            <div className="flex items-start justify-between gap-3">
              <span
                className="flex size-10 items-center justify-center rounded-sm border border-border bg-background text-body"
                aria-hidden
              >
                {currentTier === "premium" ? <Crown className="size-5" /> : <Wallet className="size-5" />}
              </span>
              <TierBadge tier={currentTier as any} verificationStatus={user?.verificationStatus} />
            </div>

            <div>
              <p className="eyebrow">Current balance</p>
              <p className="mt-1 text-[32px] leading-10 font-normal tracking-[-0.8px] text-foreground tabular-nums">
                0.00 <span className="text-base text-muted-foreground">TZS</span>
              </p>
            </div>

            <div className="border-t border-border pt-4">
              <p className="text-sm font-medium text-foreground">{user?.name}</p>
              <p className="text-caption text-muted-foreground">
                {currentTier === "free" && user?.verificationStatus === "verified"
                  ? "Verified Student"
                  : tiers.find((t: any) => t.id === currentTier)?.name || "Free Student Tier"}
              </p>
            </div>
          </section>

          {user?.verificationStatus !== "verified" && (
            <Alert variant="info">
              <Info />
              <div>
                <AlertTitle>Action required</AlertTitle>
                <AlertDescription>
                  {user?.verificationStatus === "pending_review"
                    ? "Your documents are currently being processed by our team."
                    : "Verify your identity to unlock payouts and verified status."}
                </AlertDescription>
              </div>
            </Alert>
          )}
        </div>

        {/* Right column: tiers */}
        <div className="space-y-6 lg:col-span-2">
          <SectionCard title="Membership plans" noPadding>
            {pricingLoading ? (
              <div className="p-4 md:p-5">
                <PageSkeleton />
              </div>
            ) : (
              <ul className="divide-y divide-border">
                {tiers.map((tier: any) => {
                  const isActive = tier.id === currentTier;
                  const amount = getAmountForTier(tier.id);

                  const tierRanks: Record<string, number> = {
                    free: 0,
                    verified: 1,
                    premium: 2,
                  };
                  const currentRank = tierRanks[currentTier] || 0;
                  const targetRank = tierRanks[tier.id] || 0;

                  return (
                    <li
                      key={tier.id}
                      className={cn(
                        "grid items-center gap-4 p-4 sm:grid-cols-[1fr_auto_10rem] md:px-5",
                        isActive && "bg-accent/40",
                      )}
                    >
                      <div className="flex min-w-0 items-center gap-3">
                        <span
                          className="flex size-9 shrink-0 items-center justify-center rounded-sm border border-border bg-background text-body"
                          aria-hidden
                        >
                          {tier.id === "premium" ? <Crown className="size-4" /> : <BadgeCheck className="size-4" />}
                        </span>
                        <div className="min-w-0">
                          <div className="flex flex-wrap items-center gap-2">
                            <span className="text-sm font-medium text-foreground">{tier.name}</span>
                            {isActive && <Badge variant="default">Current</Badge>}
                          </div>
                          <p className="text-[13px] text-muted-foreground">{tier.features[0]}</p>
                        </div>
                      </div>

                      <div className="sm:text-right">
                        <p className="font-mono text-sm text-foreground tabular-nums">{tier.priceLabel}</p>
                        <p className="eyebrow">One-time fee</p>
                      </div>

                      <div className="sm:text-right">
                        {isActive ? (
                          <span className="inline-flex items-center gap-1.5 text-sm font-medium text-success">
                            <CheckCircle2 className="size-4" aria-hidden />
                            Active
                          </span>
                        ) : targetRank > currentRank ? (
                          <Button className="w-full" onClick={() => handleUpgrade(tier.id)}>
                            Upgrade
                            <ArrowRight />
                          </Button>
                        ) : (
                          <Button className="w-full" variant="outline" disabled>
                            {targetRank < currentRank ? "Included" : "Current"}
                          </Button>
                        )}
                      </div>
                    </li>
                  );
                })}
              </ul>
            )}
          </SectionCard>

          <section aria-labelledby="benefits-heading">
            <h2 id="benefits-heading" className="mb-3 text-[15px] font-medium text-foreground">
              Platform benefits
            </h2>
            <ul className="grid gap-3 md:grid-cols-3">
              {[
                { title: "Direct Payouts", desc: "Withdraw earnings instantly to Mobile Money." },
                { title: "Priority Support", desc: "Get help from dedicated account managers." },
                { title: "Verified Badge", desc: "Build trust with institutional employers." },
              ].map((benefit, i) => (
                <li key={i} className="rounded-md border border-border p-4">
                  <p className="mb-1 text-sm font-medium text-foreground">{benefit.title}</p>
                  <p className="text-[13px] text-muted-foreground">{benefit.desc}</p>
                </li>
              ))}
            </ul>
          </section>
        </div>
      </div>

      <PaymentModal
        open={upgradeOpen}
        onClose={() => setUpgradeOpen(false)}
        onSubmit={handlePaymentSuccess}
        amount={getAmountForTier(selectedTier)}
        description={
          selectedTier.charAt(0).toUpperCase() + selectedTier.slice(1) + " Membership Upgrade"
        }
        courseName={tiers.find((t: any) => t.id === selectedTier)?.name || "Membership Upgrade"}
      />
    </div>
  );
}

export default StudentWallet;
